import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import { cargarTablaPorCultivoAsync } from "../../../../../modulos/catalogos/monitoreo/services/MonitoreoCultivoPlagaGrpcCatalogService";
import { monitoreoPlagaEnfermedadGrpcCatalogService } from "../../../../../modulos/catalogos/monitoreo/services/MonitoreoPlagaEnfermedadGrpcCatalogService";
import {
  fusionarInformeSemanal,
  InformeSemanal,
} from "../../../../../modulos/monitoreo/sesion/informeSemanal";
import { sesionDetalleGrpcService } from "../../../../../modulos/monitoreo/sesion/services/SesionDetalleGrpcService";
import { fx_createLoteGrpcService } from "../../../../../modulos/terceros/services/LoteGrpcService";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";

/** Lo que hace falta para armar el documento del reporte. */
export type DatosInforme = {
  informe: InformeSemanal;
  plagas: MonitoreoPlagaEnfermedadItem[];
  /** Contornos de TODOS los lotes de la finca, ya en lat/lng, para dibujarlos
   *  bajo los árboles en el mapa de finca. Van todos y no solo los monitoreados:
   *  el valor del mapa es ubicarse en la finca entera. Vacío si no se pudieron
   *  cargar — el reporte se dibuja igual, sin contornos. */
  poligonos: { lat: number; lng: number }[][];
};

/**
 * Trae el informe de una semana: el detalle de cada recorrido, fusionado, más
 * el catálogo de plagas.
 *
 * Lo consumen los **dos** caminos del reporte —el `<iframe>` de la pantalla y
 * la pestaña imprimible—, para que no puedan divergir en cómo se arma el
 * informe.
 *
 * Los detalles se piden en paralelo: son pocos (una semana rara vez pasa de
 * cuatro recorridos) y en serie sumarían latencia sin necesidad.
 *
 * @param sesionIds Sesiones de la semana.
 * @throws Si falla alguna petición, o si `sesionIds` viene vacío.
 */
export async function cargarInformeSemanalAsync(
  sesionIds: string[],
): Promise<DatosInforme> {
  const [detalles, plagas, poligonos] = await Promise.all([
    Promise.all(sesionIds.map((id) => sesionDetalleGrpcService.getAsync(id))),
    monitoreoPlagaEnfermedadGrpcCatalogService.getListAsync(),
    _cargarPoligonosLotesAsync(),
    cargarTablaPorCultivoAsync(),
  ]);

  const informe = fusionarInformeSemanal(detalles);
  if (!informe) throw new Error("La semana no tiene recorridos que informar.");

  return { informe, plagas, poligonos };
}

/**
 * Contornos de los lotes de la finca seleccionada, convertidos de GeoJSON
 * (`[lng, lat]`) a lat/lng, que es lo que espera Leaflet.
 *
 * Usa el **mismo servicio cacheado que ya dibuja el mapa de la app**, así que en
 * la práctica no agrega una petición. Si falla, devuelve vacío en vez de
 * propagar: unos contornos son un apoyo visual, no una razón para quedarse sin
 * informe.
 */
async function _cargarPoligonosLotesAsync(): Promise<{ lat: number; lng: number }[][]> {
  try {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    const lotes = await fx_createLoteGrpcService(finca.id).getListAsync();
    return lotes.flatMap((lote) =>
      (lote.areaGeograficaPolygon?.coordinates ?? []).map((anillo) =>
        anillo.map(([lng, lat]) => ({ lat, lng })),
      ),
    );
  } catch {
    return [];
  }
}
